import { useLayoutEffect, useRef } from 'react';

import classNames from 'classnames/bind';

import styles from 'shared_DEPRECATED/components/Typography/Typography.module.css';

export const colors = {
  black: 'var(--fgPrimary)',
  blackTotal: 'var(--black)',
  gray: 'var(--fgSecondary)',
  grayTotal: 'var(--gray500)',
  lightGray: 'var(--bgCompSecondaryTiny)',
  green: 'var(--fgSuccess)',
  orange: 'var(--fgWarning)',
  red: 'var(--fgDanger)',
  blue: 'var(--fgInteractive)',
  white: 'var(--fgPrimaryInverse)',
  whiteTotal: 'var(--white)',
  inherit: 'inherit',
} as const;

const typographyTypes = {
  xsm: {
    'font-size': '0.625rem',
    'line-height': '0.75rem',
  },
  small: {
    'font-size': '0.75rem',
    'line-height': '1rem',
  },
  meta: {
    'font-size': '0.875rem',
    'line-height': '1.25rem',
  },
  body: {
    'font-size': '1rem',
    'line-height': '1.5rem',
  },
  smallHeading: {
    'font-size': '1rem',
    'line-height': '1.25rem',
  },
  heading: {
    'font-size': '1.25rem',
    'line-height': '1.5rem',
  },
  semiLargeHeading: {
    'font-size': '1.5rem',
    'line-height': '1.75rem',
  },
  largeHeading: {
    'font-size': '2rem',
    'line-height': '2.25rem',
  },
  extraLargeHeading: {
    'font-size': '2.5rem',
  },
  inherit: {
    'font-size': 'inherit',
    'line-height': 'inherit',
  },
};

const fontWeights = {
  regular: '400',
  medium: '500',
  semibold: '600',
  bold: '700',
  inherit: 'inherit',
} as const;

const textAlignProps = {
  start: 'start',
  end: 'end',
  left: 'left',
  right: 'right',
  center: 'center',
  justify: 'justify',
  justifyAll: 'justify-all',
  matchParent: 'match-parent',
} as const;

const whiteSpaceProps = {
  initial: 'initial',
  nowrap: 'nowrap',
  breakSpaces: 'break-spaces',
  pre: 'pre',
  preLine: 'pre-line',
  preWrap: 'pre-wrap',
} as const;

const overflowWrapProps = {
  initial: 'initial',
  normal: 'normal',
  anywhere: 'anywhere',
  breakWord: 'break-word',
} as const;

const textTransformProps = {
  initial: 'initial',
  lowercase: 'lowercase',
  uppercase: 'uppercase',
  capitalize: 'capitalize',
} as const;

const wordBreakProps = {
  initial: 'initial',
  normal: 'normal',
  breakAll: 'break-all',
  keepAll: 'keep-all',
  breakWord: 'break-word',
} as const;

const cx = classNames.bind(styles);

type TypographyProps = {
  as?: React.ElementType;
  title?: string;
  children: React.ReactNode;
  color?: keyof typeof colors;
  hoverColor?: keyof typeof colors;
  fontWeight?: keyof typeof fontWeights;
  textAlign?: keyof typeof textAlignProps;
  type?: keyof typeof typographyTypes;
  whiteSpace?: keyof typeof whiteSpaceProps;
  overflowWrap?: keyof typeof overflowWrapProps;
  textTransform?: keyof typeof textTransformProps;
  wordBreak?: keyof typeof wordBreakProps;
  cropped?: boolean;
  dataTestid?: string | null;
};

const Typography = ({
  as: Component = 'p',
  title = '',
  children,
  color = colors.inherit,
  fontWeight = fontWeights.inherit,
  textAlign = textAlignProps.start,
  whiteSpace = whiteSpaceProps.initial,
  overflowWrap = overflowWrapProps.initial,
  textTransform = textTransformProps.initial,
  wordBreak = wordBreakProps.normal,
  type = 'inherit',
  cropped = false,
  dataTestid = null,
  hoverColor,
}: TypographyProps) => {
  const element = useRef<HTMLElement | null>(null);
  const classNames = cx('typography', { 'typography--cropped': cropped });

  useLayoutEffect(() => {
    const elementRef = element.current;

    if (elementRef) {
      const fontProps = typographyTypes[type];
      Object.entries(fontProps).forEach(([prop, value]) =>
        elementRef.style.setProperty(`--${prop}`, value)
      );
      elementRef.style.setProperty('--font-weight', fontWeights[fontWeight]);
      elementRef.style.setProperty('--text-align', textAlignProps[textAlign]);
      elementRef.style.setProperty(
        '--white-space',
        whiteSpaceProps[whiteSpace]
      );
      elementRef.style.setProperty(
        '--overflow-wrap',
        overflowWrapProps[overflowWrap]
      );
      elementRef.style.setProperty(
        '--text-transform',
        textTransformProps[textTransform]
      );
      elementRef.style.setProperty('--word-break', wordBreakProps[wordBreak]);
    }
  }, [
    element,
    fontWeight,
    textAlign,
    type,
    whiteSpace,
    overflowWrap,
    textTransform,
    wordBreak,
  ]);

  useLayoutEffect(() => {
    const elementRef = element.current;

    if (elementRef) {
      elementRef.style.setProperty('--color', colors[color]);
      elementRef.style.setProperty(
        '--hover-color',
        colors[hoverColor || color]
      );
    }
  }, [color, element, hoverColor]);

  return (
    <Component
      className={classNames}
      ref={element}
      title={title}
      data-testid={dataTestid}
    >
      {children}
    </Component>
  );
};

export default Typography;
